/**
 * Normalise the `message` field of a NestJS error response into display text.
 *
 * NestJS sends a plain string for most errors, but class-validator failures come
 * back as a `string[]` — one entry per failing constraint (see the backend's
 * `validationExceptionFactory`, which throws `new BadRequestException(messages)`).
 * A caller that assumes a string will happily assign that array into string-typed
 * state and render it, which is how an array ends up displayed raw in the UI.
 *
 * The same constraint repeats once per offending row, so entries are deduped, and
 * joined with `; ` rather than a bare comma so a multi-constraint failure reads as
 * a sentence.
 *
 * Returns `null` rather than a fallback string when there is no usable message, so
 * each caller supplies its own context-appropriate default.
 */
export function normaliseApiErrorMessage(message: unknown): string | null {
  if (typeof message === 'string') {
    return message.trim().length > 0 ? message : null;
  }

  if (Array.isArray(message)) {
    const entries = Array.from(
      new Set(
        message.filter(
          (entry): entry is string =>
            typeof entry === 'string' && entry.trim().length > 0
        )
      )
    );

    return entries.length > 0 ? entries.join('; ') : null;
  }

  return null;
}
